import { useEffect, type ReactNode } from 'react';
import { cx } from '../lib/cx';

/**
 * Right-side overlay drawer for detail views (Doc Review Pane, entity quick-look).
 * No portal — renders in flow so the page can dim behind it. Pair with `open` boolean.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
  width = 'md',
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  width?: 'md' | 'lg';
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;
  const w = width === 'lg' ? 'w-[760px]' : 'w-[520px]';

  return (
    <div className="fixed inset-0 z-40">
      <div
        className="absolute inset-0 bg-[color:var(--v2-ink)]/20 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-label={typeof title === 'string' ? title : undefined}
        className={cx(
          'v2-card absolute right-3 top-3 bottom-3 flex flex-col',
          w,
        )}
      >
        <header className="flex items-center justify-between border-b border-[color:var(--v2-stroke)] px-4 py-3">
          <div className="text-sm font-bold text-[color:var(--v2-ink)]">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-[color:var(--v2-ink-soft)] hover:bg-[color:var(--v2-sunk)] hover:text-[color:var(--v2-ink)]"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M6 6l12 12M18 6l-12 12" />
            </svg>
          </button>
        </header>
        <div className="flex-1 overflow-auto p-4">{children}</div>
        {footer && (
          <footer className="border-t border-[color:var(--v2-stroke)] px-4 py-3">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}
